import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePlans } from '../../hooks/usePlans';
import { usePlanPermissions } from '../../hooks/usePlanPermissions';
import { customersService, invoicesService, inventoryService, resolveTenantId, settingsService } from '../../services/database';
import { supabase } from '../../lib/supabase';
import InitialPlanSelectionModal from '../common/InitialPlanSelectionModal';

interface DashboardLayoutProps {
  children: React.ReactNode;
  hideSidebar?: boolean;
}

export function DashboardLayout({ children, hideSidebar = false }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications] = useState<Array<{ title: string; time: string; type: 'info'|'warning'|'success' }>>([]);
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [isHtcPortalOnly, setIsHtcPortalOnly] = useState(() => {
    try {
      return localStorage.getItem('htc_portal_only') === '1';
    } catch {
      return false;
    }
  });
  const [userProfile, setUserProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    address: '',
    city: '',
    country: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [saveMessage, setSaveMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { currentPlan, getTrialStatus, trialInfo, hasUsedTrial, startTrialWithPlan, hasAccess } = usePlans();
  const { currentPlanId, canAccessRoute, getRequiredPlanForRoute } = usePlanPermissions();
  const [kpiCounts, setKpiCounts] = useState({ invoices: 0, customers: 0, products: 0 });
  const trialStatus = getTrialStatus();
  const [allowedModules, setAllowedModules] = useState<Set<string> | null>(null);
  const [isOwner, setIsOwner] = useState(true); // Por defecto true hasta verificar
  const isAdminAllowed = Boolean(allowedModules?.has('admin'));
  const isPrivilegedUser = Boolean(isOwner || isAdminAllowed);
  const effectiveHasAccess = () => (isPrivilegedUser ? true : hasAccess());
  const [restrictedModal, setRestrictedModal] = useState<{ show: boolean; moduleName: string; requiredPlan: string }>({
    show: false,
    moduleName: '',
    requiredPlan: ''
  });
  const [showInitialPlanModal, setShowInitialPlanModal] = useState(false);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  useEffect(() => {
    const loadHtcFlag = async () => {
      try {
        if (!user?.id) {
          setIsHtcPortalOnly(false);
          try { localStorage.removeItem('htc_portal_only'); } catch {}
          return;
        }

        try {
          const { data: authData, error: authErr } = await supabase.auth.getUser();
          const metaFlag = !authErr && Boolean((authData as any)?.user?.user_metadata?.htc_portal_only);
          if (metaFlag) {
            setIsHtcPortalOnly(true);
            try { localStorage.setItem('htc_portal_only', '1'); } catch {}
            return;
          }
        } catch {
          // ignore and fallback to public.users
        }

        try {
          const { data: roleRows, error: roleErr } = await supabase
            .from('user_roles')
            .select('id, roles!inner(name)')
            .eq('user_id', user.id);

          const isHtcRole = !roleErr && Array.isArray(roleRows) && roleRows.some((r: any) => String(r?.roles?.name || '').toLowerCase() === 'htc_portal');
          if (isHtcRole) {
            setIsHtcPortalOnly(true);
            try { localStorage.setItem('htc_portal_only', '1'); } catch {}
            return;
          }
        } catch {
          // ignore and fallback to public.users
        }

        const { data } = await supabase
          .from('users')
          .select('htc_portal_only')
          .eq('id', user.id)
          .maybeSingle();

        const rowFlag = Boolean((data as any)?.htc_portal_only);
        setIsHtcPortalOnly(rowFlag);
        try {
          if (rowFlag) localStorage.setItem('htc_portal_only', '1');
          else localStorage.removeItem('htc_portal_only');
        } catch {}
      } catch {
        setIsHtcPortalOnly(false);
        try { localStorage.removeItem('htc_portal_only'); } catch {}
      }
    };

    loadHtcFlag();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    if (Boolean((user as any)?.user_metadata?.htc_portal_only)) {
      setIsHtcPortalOnly(true);
      try { localStorage.setItem('htc_portal_only', '1'); } catch {}
    }
  }, [user]);

  useEffect(() => {
    setUserProfile(prev => ({
      ...prev,
      fullName: (user?.user_metadata as any)?.full_name || prev.fullName || (user?.email?.split('@')[0] ?? ''),
      email: user?.email ?? prev.email,
    }));
  }, [user]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('sendbillnow_sidebar_collapsed');
      if (stored === '1') setSidebarCollapsed(true);
    } catch {
      // ignore
    }
  }, []);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('sendbillnow_sidebar_collapsed', next ? '1' : '0');
      } catch {
        // ignore
      }
      return next;
    });
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (!profilePanelOpen || !user?.id) return;

        const tenantId = await resolveTenantId(user.id);
        const isOwnerUser = tenantId === user.id;

        // Fetch current user profile
        const { data: currentData, error: currentError } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        // Fetch owner profile when sub-user
        const { data: ownerData } = !isOwnerUser && tenantId
          ? await supabase
              .from('users')
              .select('*')
              .eq('id', tenantId)
              .maybeSingle()
          : { data: null } as any;

        const data = currentError ? null : (currentData || null);
        if (!data) return;

        let companyFromSettings: string | undefined;
        try {
          const info = await settingsService.getCompanyInfo();
          if (info && (info as any)) {
            const resolvedName =
              (info as any).name ||
              (info as any).company_name ||
              (info as any).legal_name;
            if (resolvedName) {
              companyFromSettings = String(resolvedName);
            }
          }
        } catch (e) {
          console.error('Error getting company information for My Profile:', e);
        }

        // For sub-users, show owner company/info while keeping the sub-user identity (email/name)
        const ownerInfo = ownerData || null;
        setUserProfile(prev => ({
          ...prev,
          email: data.email || user.email || prev.email,
          fullName: data.full_name || prev.fullName || (user.email?.split('@')[0] ?? ''),
          phone: (ownerInfo?.phone || data.phone || prev.phone),
          company: (ownerInfo?.company || data.company || companyFromSettings || prev.company),
          position: (ownerInfo?.position || data.position || prev.position),
          address: (ownerInfo?.address || data.address || prev.address),
          city: (ownerInfo?.city || data.city || prev.city),
          country: (ownerInfo?.country || data.country || prev.country || 'República Dominicana'),
        }));
      } catch (error) {
        console.error('Error loading profile in DashboardLayout:', error);
      }
    };

    loadProfile();
  }, [profilePanelOpen, user?.id, user?.email]);

  // Verificar si el usuario es owner
  useEffect(() => {
    const checkIfOwner = async () => {
      if (!user?.id) return;
      try {
        const tenantId = await resolveTenantId(user.id);
        setIsOwner(tenantId === user.id);
      } catch (error) {
        console.error('Error verificando si es owner:', error);
        // Fail-open for owners: keep previous value to avoid intermittent blocks
        setIsOwner(prev => prev);
      }
    };
    checkIfOwner();
  }, [user?.id]);

  // Verificar si es usuario nuevo y mostrar modal de selección de plan
  useEffect(() => {
    if (!user?.id) return;

    if (isPrivilegedUser || isHtcPortalOnly) {
      setShowInitialPlanModal(false);
      setShowTrialExpiredModal(false);
      return;
    }
    
    // Verificar si ya usó el trial
    const trialUsed = hasUsedTrial();
    const savedTrialInfo = localStorage.getItem('contard_trial_info');
    
    // Si no ha usado el trial y no hay info guardada, es un usuario nuevo
    if (!trialUsed && !savedTrialInfo) {
      setShowInitialPlanModal(true);
    }
    
    // Si el trial expiró y no tiene plan activo, mostrar modal de bloqueo
    // Pero no mostrar si el usuario está en páginas permitidas
    const allowedPaths = ['/plans', '/statistics', '/profile', '/dashboard', '/referrals'];
    const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));
    if (trialInfo.hasExpired && !currentPlan?.active && !isAllowedPath) {
      setShowTrialExpiredModal(true);
    }
  }, [user?.id, hasUsedTrial, trialInfo.hasExpired, currentPlan?.active, location.pathname, isPrivilegedUser, isHtcPortalOnly]);

  useEffect(() => {
    const STORAGE_PREFIX = 'contabi_rbac_';
    const fetchAllowed = async () => {
      try {
        if (!user?.id) {
          // local fallback
          const perms = JSON.parse(localStorage.getItem(STORAGE_PREFIX + 'permissions') || '[]');
          const rolePerms = JSON.parse(localStorage.getItem(STORAGE_PREFIX + 'role_permissions') || '[]');
          const userRoles = JSON.parse(localStorage.getItem(STORAGE_PREFIX + 'user_roles') || '[]');
          const myRoleIds = userRoles.filter((ur: any) => ur.user_id === 'local').map((ur: any) => ur.role_id);
          const permIds = rolePerms.filter((rp: any) => myRoleIds.includes(rp.role_id)).map((rp: any) => rp.permission_id);
          const modules = new Set<string>();
          perms.forEach((p: any) => { if (p.action === 'access' && permIds.includes(p.id)) modules.add(p.module); });
          setAllowedModules(modules);
          return;
        }
        // Try resolve roles by user id, and fallback to email (support invite-by-email rows)
        const ownerId = await resolveTenantId(user.id);
        let roleIds: string[] = [];
        const { data: urById } = await supabase.from('user_roles').select('*').eq('user_id', user.id);
        roleIds = (urById || []).map((r: any) => r.role_id);
        if (roleIds.length === 0 && user.email) {
          const { data: urByEmail } = await supabase.from('user_roles').select('*').eq('user_id', user.email);
          roleIds = (urByEmail || []).map((r: any) => r.role_id);
        }
        if (roleIds.length === 0) { setAllowedModules(new Set()); return; }

        // IMPORTANT: role_permissions is tenant-scoped; filter by owner_user_id to satisfy RLS
        const rpQuery = supabase
          .from('role_permissions')
          .select('permission_id')
          .in('role_id', roleIds);
        const { data: rp } = ownerId ? await rpQuery.eq('owner_user_id', ownerId) : await rpQuery;
        const permIds = (rp || []).map((r: any) => r.permission_id);
        if (permIds.length === 0) { setAllowedModules(new Set()); return; }
        const { data: perms } = await supabase.from('permissions').select('*').in('id', permIds).eq('action', 'access');
        setAllowedModules(new Set((perms || []).map((p: any) => p.module)));
      } catch {
        setAllowedModules(new Set());
      }
    };
    fetchAllowed();

    const onFocus = () => {
      fetchAllowed();
    };

    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const uid = user?.id || '';
        if (!uid) {
          setKpiCounts({ invoices: 0, customers: 0, products: 0 });
          return;
        }
        const [invoices, customers, items] = await Promise.all([
          invoicesService.getAll(uid),
          customersService.getAll(uid),
          inventoryService.getItems(uid)
        ]);
        setKpiCounts({
          invoices: (invoices || []).length,
          customers: (customers || []).length,
          products: (items || []).length
        });
      } catch {
        setKpiCounts({ invoices: 0, customers: 0, products: 0 });
      }
    };
    fetchCounts();
  }, [user]);

  const defaultNavigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: 'ri-dashboard-line',
      current: location.pathname === '/dashboard'
    },
    {
      name: 'Statistics',
      href: '/statistics',
      icon: 'ri-bar-chart-2-line',
      current: location.pathname === '/statistics'
    },
    {
      name: 'Supplier Intelligence',
      href: '/supplier-intelligence',
      icon: 'ri-building-4-line',
      current: location.pathname.startsWith('/supplier-intelligence')
    },
    {
      name: 'Client History',
      href: '/client-history',
      icon: 'ri-file-history-line',
      current: location.pathname.startsWith('/client-history')
    },
    // ACCOUNTING MODULE (CONTADOR – USA)
    {
      name: 'Accounting',
      href: '/contador',
      icon: 'ri-calculator-line',
      current: location.pathname.startsWith('/contador'),
      submenu: [
        { name: 'Staff Report', href: '/contador/staff-report' },
        { name: 'Cash & Finance', href: '/contador/caja-finanza' },
        { name: 'Payroll', href: '/contador/nomina' },
        { name: 'Purchases & Vendors', href: '/contador/compra-proveedores' },
        { name: 'Products', href: '/contador/products' },
        { name: 'Inventory', href: '/contador/inventario' },
        { name: 'Returns', href: '/contador/devoluciones' },
        { name: 'Financial Reports', href: '/contador/reportes' },
      ]
    },
    // Accounts Receivable
    {
      name: 'Accounts Receivable',
      href: '/accounts-receivable',
      icon: 'ri-money-dollar-circle-line',
      current: location.pathname.startsWith('/accounts-receivable'),
      submenu: [
        { name: 'Receivable Invoices', href: '/accounts-receivable/invoices' },
        { name: 'Customers', href: '/accounts-receivable/customers' },
        { name: 'Customer Types', href: '/accounts-receivable/customer-types' },
        { name: 'Payment Terms', href: '/accounts-receivable/payment-terms' },
        { name: 'Payments Received', href: '/accounts-receivable/payments' },
        { name: 'Collection Receipts', href: '/accounts-receivable/receipts' },
        { name: 'Customer Advances', href: '/accounts-receivable/advances' },
        { name: 'Sales Discounts', href: '/accounts-receivable/discounts' },
        { name: 'Credit Notes', href: '/accounts-receivable/credit-notes' },
        { name: 'Debit Notes', href: '/accounts-receivable/debit-notes' },
        { name: 'AR Reports', href: '/accounts-receivable/reports' },
      ]
    },
    // 4. Accounts Payable
    {
      name: 'Accounts Payable',
      href: '/accounts-payable',
      icon: 'ri-file-list-3-line',
      current: location.pathname.startsWith('/accounts-payable'),
      submenu: [
        { name: 'Supplier Invoices', href: '/accounts-payable/invoices' },
        { name: 'AP Reports', href: '/accounts-payable/reports' },
        { name: 'Suppliers', href: '/accounts-payable/suppliers' },
        { name: 'Payment Issuance', href: '/accounts-payable/payments' },
        { name: 'Purchase Orders', href: '/accounts-payable/purchase-orders' },
        { name: 'Quotes', href: '/accounts-payable/quotes' },
        { name: 'Supplier Types', href: '/accounts-payable/supplier-types' },
        { name: 'Payment Terms', href: '/accounts-payable/payment-terms' },
        { name: 'Supplier Advances', href: '/accounts-payable/advances' },
        { name: 'Debit/Credit Notes', href: '/accounts-payable/debit-credit-notes' },
      ]
    },
    // 5. Billing
    {
      name: 'Billing',
      href: '/billing',
      icon: 'ri-file-text-line',
      current: location.pathname.startsWith('/billing'),
      submenu: [
        { name: 'Sales Report', href: '/billing/sales-reports' },
        { name: 'Commission Report', href: '/billing/commission-report' },
        { name: 'Sales Reps', href: '/billing/sales-reps' },
        { name: 'Sales Rep Types', href: '/billing/sales-rep-types' },
        { name: 'Stores / Branches', href: '/billing/stores' },
        { name: 'Invoicing', href: '/billing/invoicing' },
        { name: 'Pre-invoicing', href: '/billing/pre-invoicing' },
        { name: 'Recurring Billing', href: '/billing/recurring' },
        { name: 'Cash Closing', href: '/billing/cash-closing' },
      ]
    },
    // Job Estimate
    {
      name: 'Job Estimate',
      href: '/service-documents',
      icon: 'ri-sticky-note-line',
      current: location.pathname.startsWith('/service-documents'),
    },
    // 6. Point of Sale
    {
      name: 'Point of Sale',
      href: '/pos',
      icon: 'ri-shopping-cart-line',
      current: location.pathname.startsWith('/pos'),
      submenu: [
        { name: 'Home', href: '/pos' },
        { name: 'Pick-Up Queue', href: '/pos/pickup-queue' },
        { name: 'Delivery Queue', href: '/pos/delivery-queue' },
      ]
    },
    // Online Store
    {
      name: 'Online Store',
      href: '/online-store/settings',
      icon: 'ri-store-2-line',
      current: location.pathname.startsWith('/online-store'),
      submenu: [
        { name: 'Store Settings', href: '/online-store/settings' },
        { name: 'Orders', href: '/online-store/orders' },
      ]
    },
    // 7. Inventory
    {
      name: 'Inventory',
      href: '/inventory',
      icon: 'ri-archive-line',
      current: location.pathname.startsWith('/inventory'),
      submenu: [
        { name: 'Home', href: '/inventory' },
      ]
    },
    // 8. Cash & Finance
    {
      name: 'Cash & Finance',
      href: '/cash-finance',
      icon: 'ri-money-dollar-box-line',
      current: location.pathname.startsWith('/cash-finance'),
      submenu: [
        { name: 'Open/Close', href: '/cash-finance' },
        { name: 'Petty Cash', href: '/cash-finance/petty-cash' },
        { name: 'Expenses', href: '/cash-finance/expenses' },
        { name: 'Income', href: '/cash-finance/income' },
        { name: 'Reports', href: '/cash-finance/reports' },
      ]
    },
    // Plans
    {
      name: 'Plans',
      href: '/plans',
      icon: 'ri-vip-crown-line',
      current: location.pathname.startsWith('/plans'),
      submenu: [
        { name: 'Home', href: '/plans' },
      ]
    },
    // 12. Referrals
    {
      name: 'Referrals',
      href: '/referrals',
      icon: 'ri-share-forward-line',
      current: location.pathname.startsWith('/referrals'),
      submenu: [
        { name: 'Home', href: '/referrals' },
      ]
    },
    // 13. Users (owners only)
    ...(isOwner ? [{
      name: 'Users',
      href: '/users',
      icon: 'ri-shield-user-line',
      current: location.pathname.startsWith('/users'),
      submenu: [
        { name: 'Home', href: '/users' },
      ]
    }] : []),
    // 14. Settings (owners only)
    ...(isOwner ? [{
      name: 'Settings',
      href: '/settings',
      icon: 'ri-settings-line',
      current: location.pathname.startsWith('/settings'),
      submenu: [
        { name: 'Company', href: '/settings/company' },
        { name: 'Inventory', href: '/settings/inventory' },
        { name: 'Backups', href: '/settings/backup' }
      ]
    }] : []),
    // 15. Admin (RBAC: module 'admin')
    ...(isAdminAllowed ? [{
      name: 'Admin',
      href: '/admin/dashboard',
      icon: 'ri-admin-line',
      current: location.pathname.startsWith('/admin'),
      submenu: [
        { name: 'Dashboard', href: '/admin/dashboard' },
        { name: 'Demo Requests', href: '/admin/demo-requests' }
      ]
    }] : [])
  ];

  const navigation = isHtcPortalOnly
    ? [
        {
          name: 'HTC Service Hours',
          href: '/htc/service-hours',
          icon: 'ri-time-line',
          current: location.pathname.startsWith('/htc'),
        },
      ]
    : defaultNavigation;

  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const toggleSubmenu = (href: string) => {
    setExpandedMenus(prev => 
      prev.includes(href) 
        ? prev.filter(item => item !== href)
        : [...prev, href]
    );
  };

  const renderNavItem = (item: any) => {
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isExpanded = expandedMenus.includes(item.href);

    // RBAC filter:
    // - null => loading (show minimal items)
    // - empty Set => no permissions (show minimal items)
    // Map href segments to permission module names (handle mismatches like contador → accounting)
    const MODULE_MAP: Record<string, string> = {
      'contador': 'accounting',
      'supplier-intelligence': 'inventory',
    };
    const moduleOf = (href: string) => {
      const segment = href.split('/').filter(Boolean)[0] || 'dashboard';
      return MODULE_MAP[segment] || segment;
    };
    const minimalAllowed = new Set(['dashboard', 'profile', 'plans']);
    const currentModule = moduleOf(item.href);

    const enforcePlanInSidebar = currentPlanId === 'student' && !isAdminAllowed;

    // Owners should always see all modules EXCEPT Admin, which is always RBAC-gated.
    // Admins (isAdminAllowed) have access to ALL modules.
    const allowByRbac = isOwner
      ? (currentModule === 'admin' ? isAdminAllowed : true)
      : isAdminAllowed
        ? true // Admins can see everything
        : (allowedModules && allowedModules.size > 0
            ? allowedModules.has(currentModule)
            : minimalAllowed.has(currentModule));

    const itemAllowed = allowByRbac && (!enforcePlanInSidebar || canAccessRoute(item.href));
    if (!itemAllowed) return null;

    const submenu = hasSubmenu
      ? item.submenu.filter((s: any) => {
          const sm = moduleOf(s.href);
          const allowSubByRbac = isOwner || isAdminAllowed
            ? true
            : (allowedModules && allowedModules.size > 0
                ? allowedModules.has(sm)
                : minimalAllowed.has(sm));

          return allowSubByRbac && (!enforcePlanInSidebar || canAccessRoute(s.href));
        })
      : null;
    const hasFilteredSubmenu = submenu && submenu.length > 0;

    // Verificar si el módulo está restringido por plan
    // Los admins tienen acceso a todo, no se les aplica restricción de plan
    const isPlanRestricted = !isAdminAllowed && !canAccessRoute(item.href) && (enforcePlanInSidebar || !isOwner);
    const requiredPlan = isPlanRestricted ? getRequiredPlanForRoute(item.href) : '';

    // Handler para items restringidos
    const handleRestrictedClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setRestrictedModal({
        show: true,
        moduleName: item.name,
        requiredPlan: requiredPlan
      });
    };

    return (
      <div key={item.name} className="mb-1">
        <div className="flex items-center">
          {isPlanRestricted ? (
            // Item restringido por plan - mostrar con candado
            <div
              onClick={handleRestrictedClick}
              className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl flex-1 transition-all duration-300 text-[#a09080] hover:bg-[#ebe5d8] cursor-pointer ${sidebarCollapsed ? 'justify-center' : ''}`}
              title={`Requiere ${requiredPlan}`}
            >
              <i className={`${item.icon} ${sidebarCollapsed ? '' : 'mr-3'} text-lg flex-shrink-0 opacity-50`}></i>
              {!sidebarCollapsed && (
                <>
                  <span className="truncate opacity-70">{item.name}</span>
                  <i className="ri-lock-2-fill ml-auto text-amber-500/70 text-sm"></i>
                </>
              )}
            </div>
          ) : (
            // Item permitido
            <Link
              to={item.href}
              className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl flex-1 transition-all duration-300 ${
                item.current
                  ? 'bg-gradient-to-r from-[#008000] to-[#006600] text-white font-semibold shadow-[0_4px_15px_rgb(0,128,0,0.35)]'
                  : 'text-[#4a5a3a] hover:bg-gradient-to-r hover:from-[#e8e2d5] hover:to-[#f0ebe0] hover:text-[#2f3e1e] hover:shadow-[0_2px_8px_rgb(0,0,0,0.06)]'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              onClick={() => setSidebarOpen(false)}
              title={sidebarCollapsed ? item.name : undefined}
            >
              <i className={`${item.icon} ${sidebarCollapsed ? '' : 'mr-3'} text-lg flex-shrink-0`}></i>
              {!sidebarCollapsed && <span className="truncate">{item.name}</span>}
            </Link>
          )}
          {hasFilteredSubmenu && !isPlanRestricted && !sidebarCollapsed && (
            <button
              onClick={() => toggleSubmenu(item.href)}
              className="p-2 ml-1 text-[#7a8a6a] hover:text-[#2f3e1e] transition-all duration-300 rounded-lg hover:bg-[#e8e2d5]"
            >
              <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-sm`}></i>
            </button>
          )}
        </div>
        {hasFilteredSubmenu && isExpanded && !isPlanRestricted && !sidebarCollapsed && (
          <div className="ml-6 mt-2 space-y-1 border-l-2 border-[#c5d4a8] pl-4">
            {submenu!.map((subItem: any) => {
              // Admins y owners no tienen restricción de plan en submenús
              const isSubItemRestricted = !isAdminAllowed && !canAccessRoute(subItem.href) && (enforcePlanInSidebar || !isOwner);
              const subRequiredPlan = isSubItemRestricted ? getRequiredPlanForRoute(subItem.href) : '';

              if (isSubItemRestricted) {
                return (
                  <div
                    key={subItem.name}
                    onClick={(e) => {
                      e.preventDefault();
                      setRestrictedModal({
                        show: true,
                        moduleName: subItem.name,
                        requiredPlan: subRequiredPlan
                      });
                    }}
                    className="flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-300 text-[#a09080] hover:bg-[#ebe5d8] cursor-pointer"
                    title={`Requiere ${subRequiredPlan}`}
                  >
                    <span className="truncate opacity-70">{subItem.name}</span>
                    <i className="ri-lock-2-fill ml-auto text-amber-500/70 text-xs"></i>
                  </div>
                );
              }

              return (
                <Link
                  key={subItem.name}
                  to={subItem.href}
                  className={`block px-3 py-2 text-sm rounded-lg transition-all duration-300 ${
                    location.pathname === subItem.href
                      ? 'text-[#008000] bg-gradient-to-r from-[#e3edd3] to-[#f0f5e8] font-semibold shadow-[0_2px_6px_rgb(0,128,0,0.12)]'
                      : 'text-[#5a6a4a] hover:text-[#2f3e1e] hover:bg-gradient-to-r hover:from-[#e8e2d5] hover:to-[#f0ebe0]'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  {subItem.name}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleSignOut = async () => {
    try {
      setProfileDropdownOpen(false);
      await signOut();
      navigate('/auth/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleProfileClick = () => {
    setProfilePanelOpen(true);
    setSidebarOpen(false);
  };

  const handleEditProfile = () => {
    setEditProfileOpen(true);
  };

  const handlePlanSelection = async (planId: string) => {
    const result = await startTrialWithPlan(planId);
    if (result.success) {
      setShowInitialPlanModal(false);
      // Recargar la página para aplicar el nuevo plan
      window.location.reload();
    } else {
      setShowInitialPlanModal(false);
      setSaveMessage((result as any).error || 'Error starting trial');
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) {
      setSaveMessage('User not authenticated');
      return;
    }

    try {
      const { error } = await supabase
        .from('users')
        .update({
          full_name: userProfile.fullName,
          phone: userProfile.phone,
          company: userProfile.company,
          position: userProfile.position,
          address: userProfile.address,
          city: userProfile.city,
          country: userProfile.country || 'República Dominicana',
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      setSaveMessage('Profile updated successfully');
      setTimeout(() => {
        setSaveMessage('');
        setEditProfileOpen(false);
      }, 2000);
    } catch (error: any) {
      console.error('Error saving profile:', error);
      const message = error?.message || (error?.error_description) || 'Error updating profile';
      setSaveMessage(message);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSaveMessage('Passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setSaveMessage('Password must be at least 6 characters');
      return;
    }
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });
      if (error) throw error;
      setSaveMessage('Password updated successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      setSaveMessage('Error changing password');
    }
  };

  const handleNotificationClick = () => {
    setNotificationsOpen(!notificationsOpen);
  };

  return (
    <div className="min-h-screen flex bg-[#f6f1e3]">
      {/* Sidebar */}
      {!hideSidebar && (
        <div
          className={`fixed inset-y-0 left-0 z-40 shrink-0 ${sidebarCollapsed ? 'w-20' : 'w-72'} bg-gradient-to-b from-[#faf8f3] via-[#f5f2ea] to-[#efe9dc] border-r border-[#e0d6c4] shadow-[0_8px_30px_rgb(0,0,0,0.12)] transform overflow-x-hidden transition-all duration-500 ease-in-out ${
            sidebarOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
          } lg:translate-x-0 lg:opacity-100 lg:static lg:inset-0`}
        >
          <div className="flex flex-col h-full">
            <div className="relative flex items-center px-4 py-4 min-h-[112px] bg-gradient-to-r from-[#008000] to-[#006600] border-b border-black/10">
              <Link
                to={isHtcPortalOnly ? '/htc/service-hours' : '/dashboard'}
                onClick={() => setSidebarOpen(false)}
                className={sidebarCollapsed ? 'flex w-full items-center justify-center' : 'absolute inset-0 flex items-center justify-center'}
              >
                {sidebarCollapsed ? (
                  <img
                    src="/F4.png"
                    alt="Send Bill Now"
                    className="h-14 w-14 object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center text-center">
                    <img
                      src="/F4.png"
                      alt="Send Bill Now"
                      className="h-14 w-14 object-contain mb-1"
                    />
                    <span
                      className="text-white text-lg font-semibold leading-tight"
                      style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 800 }}
                    >
                      Send Bill Now
                    </span>
                    <span
                      className="brand-display text-white/80 text-xs font-bold italic"
                      style={{ fontFamily: '"Pacifico", serif' }}
                    >
                      A Smart Invoice & POS System
                    </span>
                  </div>
                )}
              </Link>

              <button
                onClick={toggleSidebarCollapsed}
                className={sidebarCollapsed
                  ? 'absolute bottom-2 left-1/2 -translate-x-1/2 z-10 p-1 text-white/90 hover:text-white transition-colors'
                  : 'ml-auto relative z-10 p-1 text-white/90 hover:text-white transition-colors'
                }
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <i className={`${sidebarCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-xl`}></i>
              </button>
            </div>

            <nav className={`flex-1 py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-[#c5b89a] scrollbar-track-transparent transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
              <div className="space-y-1">
                {navigation.map(renderNavItem)}

                {!isHtcPortalOnly && (
                  <div className="mb-1">
                    <button
                      onClick={handleProfileClick}
                      className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl w-full transition-all duration-300 text-[#4a5a3a] hover:bg-gradient-to-r hover:from-[#e8e2d5] hover:to-[#f0ebe0] hover:text-[#2f3e1e] hover:shadow-[0_2px_8px_rgb(0,0,0,0.06)] ${sidebarCollapsed ? 'justify-center' : ''}`}
                      title={sidebarCollapsed ? 'My Profile' : undefined}
                    >
                      <i className={`ri-user-line ${sidebarCollapsed ? '' : 'mr-3'} text-lg flex-shrink-0`}></i>
                      {!sidebarCollapsed && <span className="truncate">My Profile</span>}
                    </button>
                  </div>
                )}
              </div>
            </nav>

            <div className={`border-t border-[#e0d6c4] bg-gradient-to-r from-[#f5f2ea] to-[#efe9dc] shadow-[0_-4px_15px_rgb(0,0,0,0.05)] transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'p-3' : 'p-4'}`}>
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#008000] to-[#008000] flex items-center justify-center shadow-lg">
                  <span className="text-sm font-bold text-white">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                {!sidebarCollapsed && (
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{user?.email || 'User'}</p>
                    <p className="text-xs text-stone-500 truncate">
                      {isHtcPortalOnly
                        ? 'HTC Portal'
                        : (isAdminAllowed ? 'Admin' : (currentPlan?.name || (trialStatus === 'expired' ? 'No active plan' : 'Trial Plan')))}
                      {!isHtcPortalOnly && !isPrivilegedUser && !currentPlan && trialStatus !== 'expired' && (
                        trialInfo.daysLeft > 0
                          ? ` (${trialInfo.daysLeft}d left)`
                          : trialInfo.hoursLeft > 0
                            ? ` (${trialInfo.hoursLeft}h left)`
                            : trialInfo.minutesLeft > 0
                              ? ` (${trialInfo.minutesLeft}m left)`
                              : ''
                      )}
                    </p>
                  </div>
                )}
                <button
                  onClick={handleSignOut}
                  className={`${sidebarCollapsed ? 'ml-0' : 'ml-2'} p-2 text-stone-500 hover:text-stone-900 transition-colors duration-200 rounded-md hover:bg-stone-200`}
                  title="Sign Out"
                >
                  <i className="ri-logout-box-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Panel */}
      {profilePanelOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setProfilePanelOpen(false)}></div>
          <div className="absolute right-0 top-0 h-full w-96 bg-[#f6f1e3] shadow-xl transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">My Profile</h2>
                <button
                  onClick={() => setProfilePanelOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {!editProfileOpen ? (
                  <>
                    {/* User Info */}
                    <div className="text-center mb-6">
                      <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl font-bold text-white">
                          {userProfile.fullName.charAt(0)}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{userProfile.fullName}</h3>
                      <p className="text-sm text-gray-600">{userProfile.email}</p>
                    </div>

                    {/* Account Status */}
                    <div className="bg-[#f7f3e5] border border-[#d8cfb6] rounded-xl p-4 mb-6 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-[#3b3221]">Account Status</span>
                        <span className="text-sm font-semibold text-[#4b5f36]">Active</span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="text-center">
                          <div className="text-lg font-bold text-[#2f3e1e]">{kpiCounts.invoices}</div>
                          <div className="text-xs text-[#5c5138]">Invoices</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-[#2f3e1e]">{kpiCounts.customers}</div>
                          <div className="text-xs text-[#5c5138]">Customers</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-[#2f3e1e]">{kpiCounts.products}</div>
                          <div className="text-xs text-[#5c5138]">Products</div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="space-y-2">
                      <button 
                        onClick={handleEditProfile}
                        className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      >
                        <i className="ri-edit-line mr-3 text-blue-600"></i>
                        Edit Profile
                      </button>
                      <button 
                        onClick={() => {
                          navigate('/settings');
                        }}
                        className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      >
                        <i className="ri-settings-line mr-3 text-gray-600"></i>
                        Settings
                      </button>
                      <button 
                        onClick={() => {
                          navigate('/plans');
                        }}
                        className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      >
                        <i className="ri-vip-crown-line mr-3 text-yellow-600"></i>
                        Upgrade Plan
                      </button>
                    </div>
                  </>
                ) : (
                  /* Edit Profile Form */
                  <div className="space-y-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900">Edit Profile</h3>
                      <button
                        onClick={() => setEditProfileOpen(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <i className="ri-arrow-left-line text-xl text-gray-500"></i>
                      </button>
                    </div>

                    {saveMessage && (
                      <div className={`p-3 rounded-lg text-sm ${
                        saveMessage.includes('Error') 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {saveMessage}
                      </div>
                    )}

                    {/* Personal Information */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Personal Information</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name
                          </label>
                          <input
                            type="text"
                            value={userProfile.fullName}
                            onChange={(e) => setUserProfile({...userProfile, fullName: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={userProfile.email}
                            onChange={(e) => setUserProfile({...userProfile, email: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone
                          </label>
                          <input
                            type="tel"
                            value={userProfile.phone}
                            onChange={(e) => setUserProfile({...userProfile, phone: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Company
                          </label>
                          <input
                            type="text"
                            value={userProfile.company}
                            onChange={(e) => setUserProfile({...userProfile, company: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Position
                          </label>
                          <input
                            type="text"
                            value={userProfile.position}
                            onChange={(e) => setUserProfile({...userProfile, position: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address
                          </label>
                          <input
                            type="text"
                            value={userProfile.address}
                            onChange={(e) => setUserProfile({...userProfile, address: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              City
                            </label>
                            <input
                              type="text"
                              value={userProfile.city}
                              onChange={(e) => setUserProfile({...userProfile, city: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Country
                            </label>
                            <input
                              type="text"
                              value={userProfile.country}
                              onChange={(e) => setUserProfile({...userProfile, country: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Change Password */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-4">Change Password</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Current Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <button
                          onClick={handleChangePassword}
                          className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                        >
                          Change Password
                        </button>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={handleSaveProfile}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer - Only show when not editing */}
              {!editProfileOpen && (
                <div className="border-t border-gray-200 p-6">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  >
                    <i className="ri-logout-box-line mr-2"></i>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Top navigation */}
        <div className="flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white/95 backdrop-blur-sm px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          {!hideSidebar && (
            <>
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-700 lg:hidden hover:bg-gray-100 rounded-lg transition-colors duration-200"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <i className="ri-menu-line text-xl"></i>
              </button>

              <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />
            </>
          )}

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="relative flex flex-1 items-center">
            </div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Notifications button */}
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500 relative hover:bg-gray-100 rounded-lg transition-colors duration-200"
                onClick={handleNotificationClick}
              >
                <span className="sr-only">View notifications</span>
                <i className="ri-notification-3-line text-xl"></i>
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {notifications.length}
                  </span>
                )}
              </button>

              {/* Notifications dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 top-16 mt-2 w-80 bg-white rounded-xl shadow-xl ring-1 ring-black/5 z-50 border border-gray-100">
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Notifications</h3>
                    {notifications.length === 0 ? (
                      <div className="text-sm text-gray-500">No notifications</div>
                    ) : (
                      <div className="space-y-3">
                        {notifications.map((n, idx) => (
                          <div key={idx} className={`flex items-start space-x-3 p-3 rounded-lg transition-colors duration-200 ${n.type === 'warning' ? 'bg-yellow-50' : n.type === 'success' ? 'bg-green-50' : 'bg-blue-50'}`}>
                            <i className={`${n.type === 'warning' ? 'ri-warning-line text-yellow-500' : n.type === 'success' ? 'ri-check-line text-green-500' : 'ri-information-line text-blue-500'} mt-1`}></i>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{n.title}</p>
                              <p className="text-xs text-gray-500">{n.time}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {!hideSidebar && (
        <div 
          className={`fixed inset-0 z-30 lg:hidden transition-all duration-500 ease-in-out ${
            sidebarOpen 
              ? 'opacity-100 pointer-events-auto' 
              : 'opacity-0 pointer-events-none'
          }`}
        >
          <div 
            className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm" 
            onClick={() => setSidebarOpen(false)} 
          />
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {(profileDropdownOpen || notificationsOpen) && (
        <div 
          className="fixed inset-0 z-20" 
          onClick={() => {
            setProfileDropdownOpen(false);
            setNotificationsOpen(false);
          }} 
        />
      )}

      {/* Modal de módulo restringido */}
      {restrictedModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setRestrictedModal({ show: false, moduleName: '', requiredPlan: '' })}
          />
          
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header con gradiente */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-center">
              <div className="w-20 h-20 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm">
                <i className="ri-lock-2-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">Premium Module</h3>
              <p className="text-amber-100 text-sm">Restricted access</p>
            </div>

            {/* Contenido */}
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-gray-600 mb-4">
                  The module <span className="font-semibold text-gray-900">"{restrictedModal.moduleName}"</span> is not available in your current plan.
                </p>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center justify-center mb-2">
                    <i className="ri-vip-crown-2-fill text-amber-500 text-2xl mr-2"></i>
                    <span className="text-sm text-gray-600">Required plan:</span>
                  </div>
                  <p className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {restrictedModal.requiredPlan}
                  </p>
                </div>
              </div>

              {/* Beneficios */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">By upgrading you get:</p>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-gray-700">
                    <i className="ri-check-line text-green-500 mr-2"></i>
                    Full access to {restrictedModal.moduleName}
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <i className="ri-check-line text-green-500 mr-2"></i>
                    All premium features
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <i className="ri-check-line text-green-500 mr-2"></i>
                    Priority support
                  </li>
                </ul>
              </div>

              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={() => setRestrictedModal({ show: false, moduleName: '', requiredPlan: '' })}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setRestrictedModal({ show: false, moduleName: '', requiredPlan: '' });
                    navigate('/plans');
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <i className="ri-arrow-up-circle-line mr-2 text-2xl"></i>
                  View Plans
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de selección de plan inicial para usuarios nuevos */}
      {showInitialPlanModal && !isPrivilegedUser && !isHtcPortalOnly && (
        <InitialPlanSelectionModal onPlanSelected={handlePlanSelection} />
      )}

      {/* Modal de trial expirado - bloqueo total */}
      {showTrialExpiredModal && !effectiveHasAccess() && !isPrivilegedUser && !isHtcPortalOnly && !['/plans', '/statistics', '/profile', '/dashboard', '/referrals'].some(p => location.pathname.startsWith(p)) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-orange-600 p-8 text-center">
              <div className="w-20 h-20 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm">
                <i className="ri-time-line text-4xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Trial Period Ended</h2>
              <p className="text-red-100 text-lg">Your system access has expired</p>
            </div>
            <div className="p-8">
              <div className="text-center mb-6">
                <p className="text-gray-700 mb-4">
                  Your free trial period of <span className="font-bold">15 days</span> has ended.
                </p>
                <p className="text-gray-600 mb-6">
                  To continue using Send Bill Now, you need to select a payment plan.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg mb-6 text-left">
                  <div className="flex items-start">
                    <i className="ri-information-line text-blue-500 mr-3 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Important</p>
                      <p className="text-sm text-blue-700">
                        Select a plan to regain access to all features and continue using the system.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowTrialExpiredModal(false);
                  navigate('/plans');
                }}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center"
              >
                <i className="ri-vip-crown-line mr-2 text-2xl"></i>
                View Plans and Pricing
              </button>
              <p className="text-center text-sm text-gray-500 mt-4">
                You can only use the trial period once per account
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardLayout;
